import React, { useEffect, useState, JSX, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { style } from 'typestyle';
import { ActionButton } from '../../components/ActionButton';
import { Modal } from '../../components/Modal';
import { ADALONG_COLORS } from '../../styles';

interface AdminProps<T> {
    // CRUD methods
    search(input: string): Promise<T[]>
    // controls will appear iff the corresponding function is defined
    create?: (t: Partial<T>) => Promise<void>
    update?: (t: Partial<T>) => Promise<void>
    remove?: (t: T) => Promise<void>

    // The resource table header
    // A TableRow is expected
    displayHeader: JSX.Element

    // A resource line. onDelete needs to be called when the user wants to delete a resource
    // A TableRow is expected
    displayLine(t: T): JSX.Element[]
    getId(t: T): string

    // The resource editing window that appears on creation or update
    // It takes an instance of an item in case of update, and null in case of creation
    // onValidItem needs to be called each time the display window represents a valid item that can potentially be
    // saved. This commands the enabling/disabling of the save button.
    // onValidItem should be called each time a new valid item is produced in the window
    displayEditWindow?(t: T | null, onValidItem: (t: Partial<T> | null) => void): JSX.Element
    refreshIndex?: number

    title: string
    createButtonLabel?: string
    customSection?: JSX.Element | null
    deleteConfirmationMessage?: string
}

const searchDebounceDelayMillis = 300

export function Admin<T>(props: AdminProps<T>) {
    // The search input value (updates immediately for responsive typing)
    const [inputValue, setInputValue] = useState("")
    // The actual search query (debounced)
    const [searchQuery, setSearchQuery] = useState("")
    // The list of resources that is displayed
    const [list, setList] = useState<T[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isEditionVisible, setIsDialogVisible] = useState(false)
    // The "Are you sure you want to delete?" dialog visibility
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false)
    // The item that is currently being edited, as represented in the backend
    const [currentItem, setCurrentItem] = useState<T | null>(null)
    // The item that is currently being edited, with unsaved modifications
    const [newItem, setNewItem] = useState<Partial<T> | null>(null)
    // Timer that controls search debounce
    const [searchTimeout, setSearchTimeout] = useState<number | null>(null)
    const [isErrorVisible, setIsErrorVisible] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [isSuccessVisible, setIsSuccessVisible] = useState(false)
    const [successMessage, setSuccessMessage] = useState("")

    useEffect(() => {
        performSearch(searchQuery)
    }, [ props.refreshIndex ])

    const displayError = (msg: string): void => {
        setIsErrorVisible(true)
        setErrorMessage(msg)
    }

    const displaySuccess = (msg: string): void => {
        setIsSuccessVisible(true)
        setSuccessMessage(msg)
    }

    // onSearch implements a simple debounce logic:
    // Input value updates immediately for responsive UI
    // Actual search is debounced to reduce API calls
    const onSearch = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        const input = e.target.value
        setInputValue(input) // Update input immediately

        if (searchTimeout) {
            clearTimeout(searchTimeout)
        }

        const timer = setTimeout(function() {
            setSearchTimeout(null)
            setSearchQuery(input) // Update search query after debounce
            performSearch(input)
        }, searchDebounceDelayMillis)
        setSearchTimeout(timer as unknown as number) // this is wild, it seems that a timer only compiles if it's cast as number
    }

    const performSearch = (input: string): Promise<void> => {
        setIsLoading(true)
        return props.search(input)
            .then((ts) => setList(ts))
            .catch(e => {
                console.error('[SEARCH ERROR] Full error object:', e)
                console.error('[SEARCH ERROR] Error message:', e?.message)
                console.error('[SEARCH ERROR] Error response:', e?.response)
                console.error('[SEARCH ERROR] Error response data:', e?.response?.data)
                const errorMsg = e?.response?.data?.message || e?.message || JSON.stringify(e)
                displayError(`Error performing a search: ${errorMsg}`)
            })
            .finally(() => setIsLoading(false))
    }

    const onCreate = (): void => {
        setCurrentItem(null)
        setIsDialogVisible(true)
    }

    const onEdit = (t: T): void => {
        setCurrentItem(t)
        setIsDialogVisible(true)
    }

    const closeEdition = (): void => {
        setCurrentItem(null)
        setNewItem(null)
        setIsDialogVisible(false)
    }

    const onDelete = (t: T): void => {
        setIsConfirmationVisible(true)
        setCurrentItem(t)
    }

    const onConfirmDelete = (): Promise<void> => {
        if (!props.remove) {
            return Promise.reject("Remove function should be defined")
        }

        return props
          .remove(currentItem!)
          .then(() => performSearch(searchQuery))
          .then(() => displaySuccess("Deletion successful!"))
          .catch((e) => {
            console.error("Item deletion error", e);
            displayError(`Deletion error: ${e.message}`);
          })
          .then(closeConfirmation);
    }

    const closeConfirmation = (): void => {
        setCurrentItem(null)
        setIsConfirmationVisible(false)
    }

    const onValidNewItem = (t: Partial<T> | null): void => {
        setNewItem(t)
    }

    const onSave = (): Promise<void> => {
        const save = currentItem ? props.update : props.create
        if (!save) {
            return Promise.reject("save function should be defined")
        }

        return save(newItem!)
            .then(() => performSearch(searchQuery))
            .catch((e) => {
                console.error("Item creation error", e)
                displayError(`Creation or update error: ${JSON.stringify(e)}`)
            })
            .then(() => setIsDialogVisible(false))
            .then(() => displaySuccess("Save successful!"))
    }

    const editButton = (t: T): JSX.Element => {
        return <Link
            className={iconLink}
            onClick={() => onEdit(t)}
        >
            <EditIcon/>
        </Link>
    }

    const deleteButton = (t: T): JSX.Element => {
        return <Link
            className={iconLink}
            onClick={() => onDelete(t)}
        >
            <DeleteIcon/>
        </Link>
    }

  // Memoize the table to prevent re-rendering on every keystroke
  const tableContent = useMemo(() => {
    return (
      <TableContainer>
        <Table>
          <TableHead>{props.displayHeader}</TableHead>
          <TableBody>
            {list.map((t) => (
              <TableRow key={props.getId(t)} hover>
                {props.displayLine(t)}
                <TableCell key="last-cell">
                  {props.update && editButton(t)}
                  {props.remove && deleteButton(t)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }, [list, props.displayHeader, props.displayLine, props.getId, props.update, props.remove])

  return (
    <Box p={3}>
      <Box className={paper}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            {props.title}
          </Typography>
          {props.create && (
            <ActionButton variant="blue" onAction={onCreate}>
              {props.createButtonLabel || 'Create'}
            </ActionButton>
          )}
        </Box>
        <Box mb={2}>
          <TextField
            placeholder="Search"
            value={inputValue}
            onChange={onSearch}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
              },
            }}
          />
        </Box>

        {props.customSection}

        <Typography component="p" className={results} variant="body2" color="text.secondary" mb={2}>
          <i>{list.length} results</i>
        </Typography>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          tableContent
        )}

        {/* Edit/Create Modal with Blur Effect */}
        {isEditionVisible && (
          <Modal onClose={closeEdition}>
            <Dialog 
              open={true} 
              onClose={closeEdition} 
              maxWidth="sm" 
              fullWidth
              onClick={(e) => e.stopPropagation()}
              PaperProps={{
                sx: {
                  bgcolor: ADALONG_COLORS.DARK_BLACK,
                  color: ADALONG_COLORS.LIGHT_WHITE,
                  borderRadius: '20px',
                  border: `1px solid ${ADALONG_COLORS.LIGHT_BLACK}`,
                  backgroundImage: 'none',
                },
              }}
            >
              <DialogTitle sx={{ color: ADALONG_COLORS.LIGHT_WHITE, fontSize: '1.5rem', fontWeight: 'bold', pt: 3, px: 4, mb: 0 }}>
                {currentItem ? 'Edit Item' : 'Create Item'}
              </DialogTitle>
              <DialogContent sx={{ pt: '10px !important', pb: 3, px: 4 }}>
                {props.displayEditWindow && props.displayEditWindow(currentItem, onValidNewItem)}
              </DialogContent>
              <DialogActions sx={{ px: 4, pb: 4 }}>
                <ActionButton 
                  variant="gray" 
                  onAction={closeEdition}
                >
                  Cancel
                </ActionButton>
                <ActionButton 
                  variant="blue" 
                  onAction={onSave} 
                  disabled={!newItem}
                >
                  Save
                </ActionButton>
              </DialogActions>
            </Dialog>
          </Modal>
        )}

        {/* Delete Confirmation Modal with Blur Effect */}
        {isConfirmationVisible && (
          <Modal onClose={closeConfirmation}>
            <Dialog 
              open={true} 
              onClose={closeConfirmation}
              onClick={(e) => e.stopPropagation()}
              PaperProps={{
                sx: {
                  bgcolor: ADALONG_COLORS.DARK_BLACK,
                  color: ADALONG_COLORS.LIGHT_WHITE,
                  borderRadius: '20px',
                  border: `1px solid ${ADALONG_COLORS.LIGHT_BLACK}`,
                  backgroundImage: 'none',
                },
              }}
            >
              <DialogTitle sx={{ color: ADALONG_COLORS.LIGHT_WHITE, fontSize: '1.5rem', fontWeight: 'bold', pt: 3, px: 4 }}>
                Confirmation
              </DialogTitle>
              <DialogContent sx={{ pt: 3, px: 4 }}>
                {props.deleteConfirmationMessage || 'Are you sure you want to remove this item?'}
              </DialogContent>
              <DialogActions sx={{ px: 4, pb: 4 }}>
                <ActionButton 
                  variant="gray" 
                  onAction={closeConfirmation}
                >
                  No
                </ActionButton>
                <ActionButton 
                  variant="red" 
                  onAction={onConfirmDelete}
                >
                  Yes
                </ActionButton>
              </DialogActions>
            </Dialog>
          </Modal>
        )}

        <Snackbar
          open={isErrorVisible}
          onClose={() => setIsErrorVisible(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={10000}
        >
          <Alert severity="error" variant="filled" onClose={() => setIsErrorVisible(false)}>
            {errorMessage}
          </Alert>
        </Snackbar>
        <Snackbar
          open={isSuccessVisible}
          onClose={() => setIsSuccessVisible(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={1000}
        >
          <Alert severity="success" variant="filled" onClose={() => setIsSuccessVisible(false)}>
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}


const iconLink = style({ 
  padding: '0 6px', 
  cursor: 'pointer',
  color: ADALONG_COLORS.LIGHT_WHITE,
  $nest: {
    '& svg': {
      color: ADALONG_COLORS.LIGHT_WHITE,
    },
  },
});
const paper = style({ 
  padding: 15, 
  overflowX: 'auto',
  color: ADALONG_COLORS.LIGHT_WHITE,
});
const results = style({ color: ADALONG_COLORS.LIGHT_GRAY, marginLeft: 15, fontWeight: 'bold' });
